import { useEffect } from 'react';
import { citiesWithoutDistricts, UKRAINE_CODE } from '~/constants/common';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { useGetAddressInfo } from '../../hooks/useGetAddressInfo';
import { Button, Grid, Typography } from '@mui/material';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete';
import { FormInput } from '~/components/FormComponents/FormInput';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { Path, useFormContext, useWatch } from 'react-hook-form';
import { useAddressQuery } from '~/queries/useAddressQuery';
import { AddressTypes } from '../../forms/GeneralInfo/types';
import { generateFullAddress, getSectionFieldName } from '../../utils';
import { errorMessages } from '~/constants/errorMessages.ts';
import { TAddressField, TFormAddress } from '../../types';
import { EMPTY_OPTION } from '~/components/FormComponents/constants';
import { BaseModal } from '~/components/modals/BaseModal/BaseModal';
import { styles } from './styles';
import { get } from 'lodash';

interface Props {
  isModalOpened: boolean;
  onClose: () => void;
  section?: string;
  header: string;
  isCountrySelectorDisabled?: boolean;
  addressType: AddressTypes | null;
  onCityIdChange?: (id: number, fieldName: string) => void;
  shouldUpdateAddressCode?: boolean;
}

export const FormAddressModal = ({
  isModalOpened,
  header,
  addressType,
  onClose,
  section,
  isCountrySelectorDisabled,
  onCityIdChange,
  shouldUpdateAddressCode,
}: Props) => {
  const {
    setValue,
    control,
    trigger,
    getValues,
    setError,
    formState: { touchedFields },
  } = useFormContext<{
    address: TFormAddress;
  }>();
  const currentAddressPath = getSectionFieldName(`address.${addressType}`, section);
  const coincidencePath = getSectionFieldName('address.coincidences', section);

  const [currentAddress, coincidences] = useWatch({
    control,
    name: [
      currentAddressPath as keyof {
        address: TFormAddress;
      },
      coincidencePath as keyof {
        address: TFormAddress;
      },
    ],
  });

  const [cities, streets, addressError, cleanError] = useGetAddressInfo(
    currentAddress,
    isModalOpened
  );

  const { countryOptions, regionOptions, districtOptions } = useAddressQuery(
    currentAddress?.region?.value
  );

  const isUkraine = currentAddress?.country?.value === UKRAINE_CODE;

  useEffect(() => {
    if (currentAddress?.city?.value && shouldUpdateAddressCode && isUkraine) {
      onCityIdChange?.(+currentAddress?.city?.value, currentAddressPath + '.codeAddress');
    }
  }, [currentAddress?.city?.value, currentAddress?.country?.value, isUkraine]);

  if (!isModalOpened) return;

  const setAddressValue = (field: TAddressField, value: TAutocompleteOption | string | null) => {
    const label = (value as TAutocompleteOption)?.label;
    const fieldPath = `${currentAddressPath}.${field}`;
    const isTouched =
      get(touchedFields, fieldPath) ||
      ((currentAddress[field] as TAutocompleteOption)?.label ?? currentAddress[field]) ||
      !!label;

    if (typeof value === 'string' && !value) {
      isTouched &&
        setValue(
          `${currentAddressPath}.${field}.label` as Path<{ address: TFormAddress }>,
          label ?? '',
          {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          }
        );
    } else {
      isTouched &&
        setValue(`${currentAddressPath}.${field}` as Path<{ address: TFormAddress }>, value, {
          shouldTouch: true,
          shouldDirty: true,
          shouldValidate: true,
        });
    }
  };

  const handleCloseModal = async () => {
    const address = getValues(
      currentAddressPath as keyof {
        address: TFormAddress;
      }
    );
    const addressString = generateFullAddress(address);
    const isCoincidences = coincidences && addressType === AddressTypes.registrationAddress;
    const updateSection = currentAddressPath.replace(
      AddressTypes.registrationAddress,
      AddressTypes.permanentAddress
    ) as Path<{ address: TFormAddress }>;
    setAddressValue('address', addressString);

    setValue(`${currentAddressPath}.address` as Path<{ address: TFormAddress }>, addressString, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    const isValid = await trigger(currentAddressPath as Path<{ address: TFormAddress }>);
    setValue(currentAddressPath as Path<{ address: TFormAddress }>, address, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    if (!isValid) {
      shouldUpdateAddressCode && setAddressValue('codeAddress', null);

      setError(`${currentAddressPath}.address` as Path<{ address: TFormAddress }>, {
        message: errorMessages.ADDRESS_FIELDS,
      });

      isCoincidences &&
        shouldUpdateAddressCode &&
        setValue(`${updateSection}.codeAddress` as Path<{ address: TFormAddress }>, null);
    }

    if (isCoincidences) {
      setValue(updateSection, address, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });

      trigger(`${updateSection}.address` as Path<{ address: TFormAddress }>);
    }
    onClose();
  };

  const isDistrictDisabled =
    !currentAddress?.region?.label ||
    [
      citiesWithoutDistricts.KYIV_REGION_CODE,
      citiesWithoutDistricts.SEVASTOPOL_REGION_CODE,
    ].includes(+currentAddress?.region?.value);

  const handleChangeStreet = () => {
    const fields = ['postalCode', 'houseNumber', 'building', 'apartment'] as const;
    fields.forEach((field) => setAddressValue(field, null));
  };

  const handleChangeCity = () => {
    if (!isUkraine) return;
    setAddressValue('street', EMPTY_OPTION);
    handleChangeStreet();
  };

  const handleChangeDistrict = () => {
    if (!isUkraine) return;
    const fields = ['city', 'street'] as const;
    fields.forEach((field) => setAddressValue(field, EMPTY_OPTION));
    handleChangeStreet();
  };

  const handleChangeRegion = (value: TAutocompleteOption | TAutocompleteOption[] | null) => {
    if (!isUkraine) return;
    const fields = ['district', 'street'] as const;
    const selected = Array.isArray(value) ? value[0] : value;
    const selectedCode = Number(selected?.value);
    const isCityRegion = [
      citiesWithoutDistricts.KYIV_REGION_CODE,
      citiesWithoutDistricts.SEVASTOPOL_REGION_CODE,
    ].includes(selectedCode);

    const nextCity: TAutocompleteOption | null =
      isCityRegion && selected
        ? {
            label: selected.label,
            value: citiesWithoutDistricts.KYIV_CITY_CODE.toString(),
          }
        : null;

    (currentAddress.city?.label || isCityRegion) && setAddressValue('city', nextCity);
    fields.forEach((field) => setAddressValue(field, EMPTY_OPTION));
    handleChangeStreet();
  };

  const handleChangeCountry = () => {
    const fields = ['region', 'district', 'city', 'street'] as const;
    fields.forEach((field) => setAddressValue(field, EMPTY_OPTION));
    setAddressValue('address', null);
    setAddressValue('codeAddress', null);
    handleChangeStreet();
  };

  return (
    <>
      <BaseModal
        open={isModalOpened}
        onClose={handleCloseModal}
        data-testid="address-block-modal"
        containerProps={{ sx: styles.container }}
      >
        <Typography variant="h5">{header}</Typography>
        <Grid container spacing={3}>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormAutocomplete
              name={`${currentAddressPath}.country`}
              options={countryOptions}
              disabled={isCountrySelectorDisabled}
              inputProps={{
                placeholder: 'Оберіть країну',
              }}
              label="Країна"
              onChange={handleChangeCountry}
            />
          </Grid>
          {isUkraine ? (
            <>
              <Grid size={{ sm: 12, xs: 12, md: 6 }}>
                <FormAutocomplete
                  name={`${currentAddressPath}.region`}
                  options={regionOptions}
                  inputProps={{
                    placeholder: 'Оберіть область',
                  }}
                  label="Область"
                  onChange={handleChangeRegion}
                />
              </Grid>
              <Grid size={{ sm: 12, xs: 12, md: 6 }}>
                <FormAutocomplete
                  name={`${currentAddressPath}.district`}
                  options={districtOptions}
                  disabled={isDistrictDisabled}
                  inputProps={{
                    placeholder: 'Оберіть район області',
                  }}
                  label="Район області"
                  onChange={handleChangeDistrict}
                />
              </Grid>
              <Grid size={{ sm: 12, xs: 12, md: 6 }}>
                <FormAutocomplete
                  name={`${currentAddressPath}.city`}
                  options={cities as unknown as TAutocompleteOption[]}
                  disabled={!currentAddress?.district?.label}
                  onChange={handleChangeCity}
                  inputProps={{
                    placeholder: 'Оберіть місто або населений пункт',
                  }}
                  label="Місто або населений пункт"
                />
              </Grid>
              <Grid size={{ sm: 12, xs: 12, md: 6 }}>
                <FormAutocomplete
                  name={`${currentAddressPath}.street`}
                  options={streets as unknown as TAutocompleteOption[]}
                  disabled={!currentAddress?.city?.label}
                  shouldSaveLabel
                  inputProps={{
                    placeholder: 'Оберіть вулицю/проспект/бульвар',
                  }}
                  label="Назва вулиці/проспекта/бульвара"
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid size={{ sm: 12, xs: 12, md: 6 }}>
                <FormInput
                  name={`${currentAddressPath}.region.label`}
                  placeholder="Оберіть область"
                  label="Область"
                  fullWidth
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
              <Grid size={{ sm: 12, xs: 12, md: 6 }}>
                <FormInput
                  name={`${currentAddressPath}.district.label`}
                  placeholder="Оберіть район області"
                  label="Район області"
                  fullWidth
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
              <Grid size={{ sm: 12, xs: 12, md: 6 }}>
                <FormInput
                  name={`${currentAddressPath}.city.label`}
                  placeholder="Оберіть місто або населений пункт"
                  label="Місто або населений пункт"
                  fullWidth
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
              <Grid size={{ sm: 12, xs: 12, md: 6 }}>
                <FormInput
                  name={`${currentAddressPath}.street.label`}
                  placeholder="Оберіть вулицю/проспект/бульвар"
                  label="Назва вулиці/проспекта/бульвара"
                  fullWidth
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
            </>
          )}
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormInput
              name={`${currentAddressPath}.postalCode`}
              label="Індекс"
              placeholder="Введіть індекс"
              fullWidth
              inputProps={{
                maxLength: 40,
              }}
            />
          </Grid>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormInput
              name={`${currentAddressPath}.houseNumber`}
              label="Номер будинку"
              placeholder="Введіть номер будинку"
              fullWidth
              inputProps={{
                maxLength: 10,
              }}
            />
          </Grid>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormInput
              name={`${currentAddressPath}.building`}
              label="Номер корпуса"
              placeholder="Введіть номер корпусу"
              fullWidth
              inputProps={{
                maxLength: 10,
              }}
            />
          </Grid>
          <Grid size={{ sm: 12, xs: 12, md: 6 }}>
            <FormInput
              name={`${currentAddressPath}.apartment`}
              label="Номер квартири/офісу/приміщення"
              placeholder="Введіть номер квартири/офісу/приміщення"
              fullWidth
              inputProps={{
                maxLength: 10,
              }}
            />
          </Grid>
        </Grid>
        <Button type="button" variant="contained" onClick={handleCloseModal}>
          Продовжити
        </Button>
      </BaseModal>

      {addressError && (
        <ModalAlert
          isOpen={!!addressError}
          text={addressError as string}
          onClose={cleanError as () => void}
        />
      )}
    </>
  );
};
